/** P32 (Feb 27, 2026) — Modifier chip + cell renderer.
 *
 *  Each line of the Service Lines table has a modifier "cell" which can
 *  be in one of three visual states:
 *   1. assessment-code lock → no chips, just an italic note
 *   2. service-not-yet-selected → italic instruction
 *   3. catalog-driven chip list → ModifierChip per available modifier
 *  Active chips can be either *manually* toggled (indigo) or *locked* by
 *  the visit-sequence / variant always-apply rules (amber).
 */
import React from 'react';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../../../components/ui/tooltip';

/** Classnames for a single chip's three states. */
function modifierChipClasses(active, locked) {
  if (!active) return 'bg-white text-slate-500 border-slate-200 hover:bg-indigo-50 hover:text-indigo-700';
  if (locked) return 'bg-amber-100 text-amber-800 border-amber-300';
  return 'bg-indigo-700 text-white border-indigo-700';
}

export function ModifierChip({ mod, description, active, locked, onClick, testId }) {
  const cls = modifierChipClasses(active, locked);
  return (
    <TooltipProvider delayDuration={250}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            disabled={locked}
            onClick={onClick}
            data-testid={testId}
            className={`px-2 py-0.5 text-xs font-mono rounded border transition-colors ${cls} ${locked ? 'cursor-default' : 'cursor-pointer'}`}
          >
            {mod}
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">{description}</p>
          {locked ? <p className="text-[10px] italic mt-1">auto-applied</p> : null}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function ModifierCell({ line, idx, modifierAvailable, serviceCode, onToggle }) {
  if (line._locked) {
    return (
      <span className="text-xs text-slate-400 italic" data-testid={`line-modifiers-locked-${idx}`}>
        no modifiers (assessment — flat rate)
      </span>
    );
  }
  if (modifierAvailable.length === 0) {
    return (
      <span className="text-xs text-slate-400 italic" data-testid={`line-modifiers-none-${idx}`}>
        {serviceCode ? 'no chips configured' : 'pick a service code'}
      </span>
    );
  }
  return (
    <div className="flex flex-wrap gap-1" data-testid={`line-modifier-chips-${idx}`}>
      {modifierAvailable.map((m) => (
        <ModifierChip
          key={m.code}
          mod={m.code}
          description={m.description}
          active={line._activeMods.has(m.code)}
          locked={line._autoMods.has(m.code)}
          onClick={() => onToggle(idx, m.code)}
          testId={`line-${idx}-chip-${m.code}`}
        />
      ))}
    </div>
  );
}
